import CalendarsService from "services/CalendarsService";
import GroupsService from "services/GroupsService";

const { createAsyncThunk, createSlice } = require("@reduxjs/toolkit");

export const getCalendars = createAsyncThunk(
  "Calendars/getAll",
  async (_, { rejectWithValue }) => {
    try {
      const response = await CalendarsService.getCalendars();
      return response.data.calendars || response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
        "Erreur lors de la récupération des calendriers"
      );
    }
  }
);

export const getCalendarById = createAsyncThunk(
  "Calendars/getCalendarById",
  async (calendarId, { rejectWithValue }) => {
    try {
      const response = await CalendarsService.getCalendarById(calendarId);
      return response.data.calendar || response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
        "Erreur lors de la récupération du calendrier"
      );
    }
  }
);

export const createCalendar = createAsyncThunk(
  "Calendars/createCalendar",
  async (data, { rejectWithValue }) => {
    try {
      const response = await CalendarsService.createCalendar(data);
      return response.data.calendar || response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Erreur lors de la création du calendrier"
      );
    }
  }
);

export const updateCalendarById = createAsyncThunk(
  "Calendars/updateCalendarById",
  async ({ calendarId, data }, { rejectWithValue }) => {
    try {
      const response = await CalendarsService.updateCalendar(calendarId, data);
      return response.data.calendar || response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
        "Erreur lors de la mise à jour du calendrier"
      );
    }
  }
);

// Elle ne sera pas utilisée
export const deleteCalendarById = createAsyncThunk(
  "Calendars/deleteCalendarById",
  async (id, { rejectWithValue }) => {
    try {
      await CalendarsService.deleteCalendarById(id);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
        "Erreur lors de la suppression du calendrier"
      );
    }
  }
);

export const markStudentAbsence = createAsyncThunk(
  "Calendars/markStudentAbsence",
  async (data, { rejectWithValue }) => {
    try {
      const response = await CalendarsService.markStudentAbsence(data);
      return response.data.calendar || response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Erreur lors du marquage de l'absence"
      );
    }
  }
);

const fetchGroupCalendars = async (groupId, { rejectWithValue }) => {
  try {
    const response = await CalendarsService.getCalendarsByGroupId(groupId);
    const payload = response.data?.data || response.data || {};

    return {
      calendars: payload.calendars || response.data?.calendars || [],
      // Bloc renvoye par l'API : heures effectuees / restantes du groupe
      hoursSummary: payload.hoursSummary || response.data?.hoursSummary || null,
    };
  } catch (error) {
    return rejectWithValue(
      error.response?.data?.message ||
      "Erreur lors de la récupération des calendriers du groupe"
    );
  }
};

export const getCalendarsByGroupId = createAsyncThunk(
  "Calendars/getCalendarsByGroupId",
  fetchGroupCalendars
);

// Rafraichissement silencieux (sans spinner ni remise a zero du compteur) :
// utilise apres un changement de statut et en polling pendant les seances.
export const refreshCalendarsByGroupId = createAsyncThunk(
  "Calendars/refreshCalendarsByGroupId",
  fetchGroupCalendars
);

export const startCalendarSessionByGroup = createAsyncThunk(
  "Calendars/startCalendarSessionByGroup",
  async ({ groupId, calendarId }, { rejectWithValue }) => {
    try {
      const response = await GroupsService.startSession(groupId, { calendarId });
      return response?.data?.calendar || response?.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Erreur lors du demarrage de la session"
      );
    }
  }
);

export const pauseCalendarSessionByGroup = createAsyncThunk(
  "Calendars/pauseCalendarSessionByGroup",
  async ({ groupId, calendarId }, { rejectWithValue }) => {
    try {
      const response = await GroupsService.pauseSession(groupId, { calendarId });
      return response?.data?.calendar || response?.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Erreur lors de la mise en pause de la session"
      );
    }
  }
);

export const completeCalendarSessionByGroup = createAsyncThunk(
  "Calendars/completeCalendarSessionByGroup",
  async ({ groupId, calendarId }, { rejectWithValue }) => {
    try {
      const response = await GroupsService.completeSession(groupId, { calendarId });
      return response?.data?.calendar || response?.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Erreur lors de la completion de la session"
      );
    }
  }
);

export const updateCalendarSessionStatusByGroup = createAsyncThunk(
  "Calendars/updateCalendarSessionStatusByGroup",
  async ({ groupId, calendarId, status, startTime, endTime, completedAt }, { rejectWithValue }) => {
    try {
      const payload = {
        calendarId,
        status,
      };

      if (startTime) {
        payload.startTime = startTime;
      }

      if (endTime) {
        payload.endTime = endTime;
      }

      if (completedAt) {
        payload.completedAt = completedAt;
      }

      const response = await GroupsService.updateAdminSession(groupId, {
        ...payload,
      });
      return response?.data?.calendar || response?.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Erreur lors de la mise a jour du statut de la session"
      );
    }
  }
);

const upsertCalendarInState = (state, payload) => {
  const updatedCalendar = payload?.calendar || payload;
  if (!updatedCalendar?._id) {
    return;
  }
  state.calendars = state.calendars.map((calendar) =>
    calendar._id === updatedCalendar._id ? updatedCalendar : calendar
  );
  if (state.calendar && state.calendar._id === updatedCalendar._id) {
    state.calendar = updatedCalendar;
  }
};


const initialState = {
  calendars: [],
  calendar: null,
  // Resume des heures du groupe courant (null pour les cours de soutien)
  groupHoursSummary: null,
  loading: false,
  error: null,
  absenceLoading: false,
};

const CalendarsSlice = createSlice({
  name: "Calendars",
  initialState,
  reducers: {
    setCalendars: (state, { payload }) => {
      state.calendars = payload;
    },
    setCalendar: (state, { payload }) => {
      state.calendar = payload;
    },
    setLoading: (state, { payload }) => {
      state.loading = payload;
    },
    setError: (state, { payload }) => {
      state.error = payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // getCalendars
      .addCase(getCalendars.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCalendars.fulfilled, (state, { payload }) => {
        state.calendars = payload;
        state.loading = false;
      })
      .addCase(getCalendars.rejected, (state, { payload }) => {
        state.error = payload;
        state.loading = false;
      })

      // getCalendarById
      .addCase(getCalendarById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCalendarById.fulfilled, (state, { payload }) => {
        state.calendar = payload;
        state.loading = false;
      })
      .addCase(getCalendarById.rejected, (state, { payload }) => {
        state.error = payload;
        state.loading = false;
      })

      // createCalendar
      .addCase(createCalendar.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCalendar.fulfilled, (state, { payload }) => {
        // Adapter selon la structure de la réponse du serveur
        const calendar = payload.calendar || payload;
        state.calendars.push(calendar);
        state.loading = false;
      })
      .addCase(createCalendar.rejected, (state, { payload }) => {
        state.error = payload;
        state.loading = false;
      })

      // updateCalendarById
      .addCase(updateCalendarById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCalendarById.fulfilled, (state, { payload }) => {
        // Adapter selon la structure de la réponse du serveur
        // const updatedCalendar = payload.calendar || payload;
        // state.calendars = state.calendars.map((calendar) =>
        //   calendar._id === updatedCalendar._id ? updatedCalendar : calendar
        // );
        // if (state.calendar && state.calendar._id === updatedCalendar._id) {
        //   state.calendar = updatedCalendar;
        // }
        state.loading = false;
      })
      .addCase(updateCalendarById.rejected, (state, { payload }) => {
        state.error = payload;
        state.loading = false;
      })

      // deleteCalendarById
      .addCase(deleteCalendarById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCalendarById.fulfilled, (state, { payload }) => {
        state.calendars = state.calendars.filter(
          (calendar) => calendar._id !== payload
        );
        if (state.calendar && state.calendar._id === payload) {
          state.calendar = null;
        }
        state.loading = false;
      })
      .addCase(deleteCalendarById.rejected, (state, { payload }) => {
        state.error = payload;
        state.loading = false;
      })

      // markStudentAbsence
      .addCase(markStudentAbsence.pending, (state) => {
        state.absenceLoading = true;
        state.error = null;
      })
      .addCase(markStudentAbsence.fulfilled, (state, { payload }) => {
        const updatedCalendar = payload.calendar || payload;
        state.calendars = state.calendars.map((calendar) =>
          calendar._id === updatedCalendar._id ? updatedCalendar : calendar
        );
        if (state.calendar && state.calendar._id === updatedCalendar._id) {
          state.calendar = updatedCalendar;
        }
        state.absenceLoading = false;
      })
      .addCase(markStudentAbsence.rejected, (state, { payload }) => {
        state.error = payload;
        state.absenceLoading = false;
      })
      // getCalendarByGroupId
      .addCase(getCalendarsByGroupId.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.groupHoursSummary = null;
      })
      .addCase(getCalendarsByGroupId.fulfilled, (state, { payload }) => {
        state.calendars = payload?.calendars || [];
        state.groupHoursSummary = payload?.hoursSummary || null;
        state.loading = false;
      })
      .addCase(refreshCalendarsByGroupId.fulfilled, (state, { payload }) => {
        state.calendars = payload?.calendars || [];
        state.groupHoursSummary = payload?.hoursSummary || null;
      })
      .addCase(getCalendarsByGroupId.rejected, (state, { payload }) => {
        state.error = payload;
        state.groupHoursSummary = null;
        state.loading = false;
      })
      .addCase(startCalendarSessionByGroup.fulfilled, (state, { payload }) => {
        upsertCalendarInState(state, payload);
      })
      .addCase(startCalendarSessionByGroup.rejected, (state, { payload }) => {
        state.error = payload;
      })
      .addCase(pauseCalendarSessionByGroup.fulfilled, (state, { payload }) => {
        upsertCalendarInState(state, payload);
      })
      .addCase(pauseCalendarSessionByGroup.rejected, (state, { payload }) => {
        state.error = payload;
      })
      .addCase(completeCalendarSessionByGroup.fulfilled, (state, { payload }) => {
        upsertCalendarInState(state, payload);
      })
      .addCase(completeCalendarSessionByGroup.rejected, (state, { payload }) => {
        state.error = payload;
      })
      .addCase(updateCalendarSessionStatusByGroup.fulfilled, (state, { payload }) => {
        upsertCalendarInState(state, payload);
      })
      .addCase(updateCalendarSessionStatusByGroup.rejected, (state, { payload }) => {
        state.error = payload;
      });
  },
});

export const { setCalendars, setCalendar, setLoading, setError, clearError } =
  CalendarsSlice.actions;

export default CalendarsSlice.reducer;
